// =========================
// EDIT PROFILE VIEW
// =========================
import App from './../../App';
import { html, render } from 'lit-html';
import { gotoRoute } from './../../Router';
import Auth from './../../Auth';
import Utils from './../../Utils';

// This is the EditProfileView class that handles the edit profile page
// This is used when the user wants to edit their profile
class EditProfileView {
  constructor() {
    this.user = null;
    this.supplier = null;
    this.services = [];
    this.errorMessage = '';
  }

  // This method initializes the view
  // It fetches the current user data and supplier data if applicable
  async init() {
    try {
      const res = await fetch(`${App.apiBase}/user/${Auth.currentUser.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch user data');
      this.user = await res.json();

      if (this.user.role === 'supplier') {
        const supplierRes = await fetch(
          `${App.apiBase}/supplier/byUser/${this.user._id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        if (supplierRes.ok) this.supplier = await supplierRes.json();

        const servicesRes = await fetch(`${App.apiBase}/service`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (servicesRes.ok) this.services = await servicesRes.json();
      }
    } catch (err) {
      console.error('Error fetching user/supplier data:', err);
      this.user = Auth.currentUser;
    }

    document.title = `PopIn – Edit Profile`;
    this.render();
    Utils.pageIntroAnim();
  }

  // This method updates the user profile
  // It handles form submission, password changes, and supplier data updates
  async updateProfile(e) {
    e.preventDefault();
    this.errorMessage = '';
    const form = e.target;
    const formData = new FormData(form);

    const oldPassword = formData.get('oldPassword');
    const newPassword = formData.get('newPassword');
    const confirmNewPassword = formData.get('confirmNewPassword');

    if (oldPassword || newPassword || confirmNewPassword) {
      if (!oldPassword || !newPassword || !confirmNewPassword) {
        this.errorMessage =
          'All password fields are required to change your password.';
        return this.render();
      }

      if (newPassword !== confirmNewPassword) {
        this.errorMessage = 'New password and confirmation do not match.';
        return this.render();
      }

      try {
        const verifyRes = await fetch(`${App.apiBase}/auth/verify-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({
            userId: this.user._id,
            password: oldPassword,
          }),
        });

        const verified = await verifyRes.json();
        if (!verified.valid) {
          this.errorMessage = 'Old password is incorrect.';
          return this.render();
        }

        formData.append('password', newPassword);
        formData.delete('oldPassword');
        formData.delete('newPassword');
        formData.delete('confirmNewPassword');
      } catch (err) {
        console.error('Password verification failed:', err);
        this.errorMessage = 'An error occurred verifying the old password.';
        return this.render();
      }
    }

    const supplierPayload = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('supplier_') && key !== 'supplier_services') {
        supplierPayload[key.replace('supplier_', '')] = value;
        formData.delete(key);
      }
    }

    const servicesSelect = form.querySelector(
      'sl-select[name="supplier_services"]'
    );
    if (servicesSelect) {
      supplierPayload.services = servicesSelect.value.map(decodeURIComponent);
    }

    const socialLinks = [];
    ['Instagram', 'TikTok', 'Facebook', 'Twitter'].forEach((platform) => {
      const field = form.querySelector(`[name="supplier_social_${platform}"]`);
      if (field && field.value.trim()) {
        socialLinks.push({ type: platform, link: field.value.trim() });
      }
    });
    supplierPayload.socialLinks = socialLinks;

    try {
      await fetch(`${App.apiBase}/user/${this.user._id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      if (this.user.role === 'supplier' && this.supplier?._id) {
        await fetch(`${App.apiBase}/supplier/${this.supplier._id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(supplierPayload),
        });
      }

      await Auth.refreshCurrentUser();

      gotoRoute(`/profile?user=${this.user._id}`);
    } catch (err) {
      console.error('Error updating profile:', err);
    }
  }

  // This method handles the image preview when a new profile image is uploaded
  handleImagePreview(e) {
    const inputEl = e.target;
    if (!inputEl.files?.[0]) return;

    const reader = new FileReader();
    reader.onload = () => {
      const imgEl = document.querySelector('.profile-image');
      if (imgEl) imgEl.src = reader.result;
    };
    reader.readAsDataURL(inputEl.files[0]);
  }

  // This method retrieves the social media link for a given platform
  getSocialLink(platform) {
    const link = (this.supplier?.socialLinks || []).find(
      (l) => l.type === platform
    );
    return link?.link || '';
  }

  render() {
    const user = this.user || {};
    const supplier = this.supplier || {};
    const profileImage = Utils.getProfileImage(user);

    const selectedServices = supplier.services || [];

    const template = html`
      <div class="layout">
        <popin-side-nav></popin-side-nav>
        <div class="main-area">
          <popin-top-bar .user=${user}></popin-top-bar>
          <div class="main-content edit-profile-page">
            <form
              @submit=${this.updateProfile.bind(this)}
              enctype="multipart/form-data"
              autocomplete="off"
            >
              <div id="edit-image">
                <label for="profileImageUpload" class="image-upload-label">
                  <img
                    src="${profileImage}"
                    alt="Profile"
                    class="profile-image"
                  />
                  <p>
                    Click your profile image to upload and change the image.
                  </p>
                </label>
                <input
                  type="file"
                  id="profileImageUpload"
                  name="profileImage"
                  accept="image/*"
                  @change=${this.handleImagePreview.bind(this)}
                  style="display: none"
                />
              </div>

              ${this.errorMessage
                ? html`
                    <sl-alert
                      variant="danger"
                      class="signup-sections"
                      open
                      closable
                    >
                      <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
                      <span .innerHTML=${this.errorMessage}></span>
                    </sl-alert>
                  `
                : ''}

              <div id="edit-main">
                <div id="edit-user">
                  <sl-input
                    name="firstName"
                    label="First Name"
                    value="${user.firstName || ''}"
                    ?disabled=${user.role === 'supplier'}
                    required
                  ></sl-input>
                  <sl-input
                    name="lastName"
                    label="Last Name"
                    value="${user.lastName || ''}"
                    ?disabled=${user.role === 'supplier'}
                    required
                  ></sl-input>
                  <sl-input
                    name="email"
                    label="Email"
                    value="${user.email || ''}"
                    required
                  ></sl-input>
                  <sl-input
                    name="location"
                    label="Location"
                    value="${user.location || ''}"
                  ></sl-input>
                  <sl-textarea
                    name="bio"
                    label="Bio"
                    .value=${user.bio || ''}
                  ></sl-textarea>
                  <input
                    type="password"
                    name="fakePassword"
                    style="display:none"
                    autocomplete="new-password"
                  />
                  <sl-input
                    name="oldPassword"
                    label="Old Password"
                    type="password"
                    placeholder="Old Password"
                    autocomplete="off"
                    toggle-password
                  ></sl-input>
                  <sl-input
                    name="newPassword"
                    label="New Password"
                    type="password"
                    placeholder="New Password"
                    toggle-password
                  ></sl-input>
                  <sl-input
                    name="confirmNewPassword"
                    type="password"
                    placeholder="Confirm Password"
                    toggle-password
                  ></sl-input>
                </div>

                ${user.role === 'supplier'
                  ? html`
                      <div id="edit-supplier">
                        <sl-input
                          name="supplier_businessName"
                          label="Business Name"
                          value="${supplier.businessName || ''}"
                          required
                        ></sl-input>
                        <sl-input
                          name="supplier_contactEmail"
                          label="Contact Email"
                          value="${supplier.contactEmail || ''}"
                          required
                        ></sl-input>
                        <sl-input
                          name="supplier_website"
                          label="Website"
                          value="${supplier.website || ''}"
                        ></sl-input>

                        <sl-select
                          name="supplier_services"
                          label="Services"
                          multiple
                          .value=${supplier.services || []}
                        >
                          ${this.services.map(
                            (service) => html`
                              <sl-option
                                value="${encodeURIComponent(service.name)}"
                                ?selected=${selectedServices.includes(
                                  service.name
                                )}
                              >
                                ${service.name}
                              </sl-option>
                            `
                          )}
                        </sl-select>

                        <sl-input
                          name="supplier_social_Instagram"
                          label="Instagram"
                          value="${this.getSocialLink('Instagram')}"
                        ></sl-input>
                        <sl-input
                          name="supplier_social_TikTok"
                          label="TikTok"
                          value="${this.getSocialLink('TikTok')}"
                        ></sl-input>
                        <sl-input
                          name="supplier_social_Facebook"
                          label="Facebook"
                          value="${this.getSocialLink('Facebook')}"
                        ></sl-input>
                        <sl-input
                          name="supplier_social_Twitter"
                          label="Twitter"
                          value="${this.getSocialLink('Twitter')}"
                        ></sl-input>
                      </div>
                    `
                  : ''}
              </div>

              <div id="edit-btn">
                <sl-button type="submit" variant="neutral"
                  >Save Changes</sl-button
                >
                <sl-button
                  variant="primary"
                  outline
                  @click=${() => gotoRoute(`/profile?user=${this.user._id}`)}
                  >Cancel</sl-button
                >
              </div>
            </form>
          </div>
        </div>
      </div>
    `;

    render(template, App.rootEl);
  }
}

export default new EditProfileView();
